import { createAction, createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import axios from "../../utils/axios";
import { IAuthActivateAccount, IAuthState, ISignupData } from "types/auth/auth.type";
import { IUser } from "types/user/user.type";
import DomainHelper from "helpers/DomainHelper";
import { getClientDTO } from './client.slice';

export interface ILoginData {
  email: string;
  password: string;
  appScheme: string;
  segmentAnonymousId: string;
}

const baseUrl = DomainHelper.getAPIMain()

const initialState: IAuthState = {
  isLoading: false,
  isSuccess: false,
  errorMessage: "",
  logout: {
    isSuccess: false,
    isLoading: false,
    isError: null,
  },
  resetPassword: {
    isSuccess: false,
    isLoading: false,
    isError: null,
  },
  verifyResetToken: {
    isLoading: false,
    isSuccess: false,
    isError: null,
  },
  resetLink: {
    isLoading: false, 
    isSuccess: false, 
    isError: null,
    isValid: false, 
  },
  completePasswordReset: {
    isLoading: false,
    isSuccess: false,
    isError: null
  },
  signUp: {
    isSuccess: false,
    isLoading: false,
    successMessage: "",
    errorMessage: "",
    user: {} as IUser
  },
  signUpCompany: {
    isSuccess: false,
    isLoading: false,
    successMessage: "",
    errorMessage: "",
    user: {} as IUser
  },
  impersonating: {
    isActive: false,
    clientId: null,
  },
};
export const signIn = createAsyncThunk("auth/signin", async (data: ILoginData, thunkAPI) => {
  try {
    const response = await axios.post(`${baseUrl}auth/login`, data);
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});
export const signUp = createAsyncThunk("auth/signUp", async (data: ISignupData, thunkAPI) => {
  try {
    const response = await axios.post(`${baseUrl}auth/register`, data, {withCredentials: true})
    return thunkAPI.fulfillWithValue(response.data);
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data);
}});
export const signUpCompany = createAsyncThunk("auth/signup/team", async (data: ISignupData, thunkAPI) => {
  try {
    const response = await axios.post(`${baseUrl}auth/signup/team`, data, {withCredentials: true})
    return thunkAPI.fulfillWithValue(response.data);
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data);
}});
export const AuthActivateAccount = createAsyncThunk("auth/activateAccount", async (data: IAuthActivateAccount, thunkAPI) => {
  try {
    const response = await axios.post(`${baseUrl}auth/activate_account`, data)
    return thunkAPI.fulfillWithValue(response.data);
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data);
}})
export const logout = createAsyncThunk("auth/logout", async (_, thunkAPI) => {
  try {
    const resp = await axios.post(`${baseUrl}auth/logout`,{},{ withCredentials: true });
    return thunkAPI.fulfillWithValue(resp.data);
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});
export const resetPassword = createAsyncThunk("auth/resetPassword", async (email: string, thunkAPI) => {
  try {
    const response = await axios.post(`${baseUrl}auth/request_new_password`, {email});
    return response.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response?.data?.message || "Email not found");
  }
});
export const completePasswordReset = createAsyncThunk(
  'auth/completePasswordReset',
  async (data: { userId: string, id: string, newPassword: string }, thunkAPI) => {
    try {
      await axios.post(`${baseUrl}auth/reset_password`, data);
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const resetPasswordResetState = createAction('auth/resetPasswordResetState');

export const verifyResetLink = createAsyncThunk(
  "auth/verifyResetLink", 
  async ({ userId, token }: { userId: string, token: string }, thunkAPI) => {
    try {
      const response = await axios.get(
        `${baseUrl}auth/verify-reset-link`, 
        { params: { userId, token } }
      );
      return response.data;
    } catch (err: any) {
      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);
export const resetVerifyResetLink = createAction('auth/resetVerifyResetLink');

export const startImpersonation = createAsyncThunk(
  'auth/startImpersonation',
  async (clientId: string, { dispatch }) => {
    await dispatch(getClientDTO(clientId));
    return clientId;
  }
);

export const endImpersonation = createAsyncThunk(
  'auth/endImpersonation',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const originalClientId = state.auth.user?.clientId;
    if (originalClientId) {
      await dispatch(getClientDTO(originalClientId));
    }
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    reset: (state) => {
      state.logout.isLoading = false;
      state.logout.isSuccess = false;
      state.logout.isError = null;
    },
    resetSignIn: (state) => {
      state.isSuccess = false;
      state.isLoading = false;
      state.errorMessage = "";
      state.user = undefined;
    },
    resetResetPassword: (state) => {
      state.resetPassword.isLoading = false;
      state.resetPassword.isSuccess = false;
      state.resetPassword.isError = null;
    },
    resetSignUp: (state) => {
      state.signUp.isLoading = false;
      state.signUp.isSuccess = false;
      state.signUp.errorMessage = "";
      state.signUp.successMessage = "";
    },
    resetSignUpCommpany: (state) => {
      state.signUpCompany.isLoading = false;
      state.signUpCompany.isSuccess = false;
      state.signUpCompany.errorMessage = "";
      state.signUpCompany.successMessage = "";
    },
    resetAuthSlice: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.errorMessage = "";
      state.logout.isSuccess = false;
      state.logout.isError = "";
      state.logout.isLoading = false;
      state.resetPassword.isSuccess = false;
      state.resetPassword.isLoading = false;
      state.resetPassword.isError = null;
      state.signUp.isSuccess = false;
      state.signUp.isLoading = false;
      state.signUp.successMessage = "";
      state.signUp.errorMessage = "";
      state.signUpCompany.isSuccess = false;
      state.signUpCompany.isLoading = false;
      state.signUpCompany.successMessage = "";
      state.signUpCompany.errorMessage = "";
      state.impersonating = {
        isActive: false,
        clientId: null,
      };
    },
    startImpersonation: (state, action: PayloadAction<string>) => {
      state.impersonating = {
        isActive: true,
        clientId: action.payload,
      };
    },
    endImpersonation: (state) => {
      state.impersonating = {
        isActive: false,
        clientId: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(signIn.pending, (state, _action) => { state.isLoading = true; });
    builder.addCase(signIn.fulfilled, (state, action) => {
      state.user = action.payload.data;
      state.isLoading = false;
      state.isSuccess = true;
      state.errorMessage = "";
      localStorage.setItem("accessToken", action.payload.data.jwtToken);
    });
    builder.addCase(signIn.rejected, (state, action) => {
      state.isLoading = false;
      //@ts-ignore
      state.errorMessage = action.payload?.message || "Something went wrong";
      state.isSuccess = false;
    });
    builder.addCase(signUpCompany.pending, (state, _action) => { state.signUpCompany.isLoading = true; });
    builder.addCase(signUpCompany.fulfilled, (state, action) => {
      state.user = action.payload.data;
      state.signUpCompany.isLoading = false;
      state.signUpCompany.isSuccess = true;
      state.signUpCompany.errorMessage = "";
      localStorage.setItem("accessToken", action.payload.data.jwtToken);
    });
    builder.addCase(signUpCompany.rejected, (state, action) => {
      state.signUpCompany.isLoading = false;
      //@ts-ignore
      state.errorMessage = action.payload?.message || "Something went wrong";
      state.isSuccess = false;
    });
    builder.addCase(logout.fulfilled, (state, action) => {
      localStorage.removeItem("accessToken");
      state.logout.isLoading = false;
      state.logout.isSuccess = true;
      state.logout.isError = action.payload?.message || "Something went wrong";
    });
    builder.addCase(logout.rejected, (state, action) => {
      localStorage.removeItem("accessToken");
      state.logout.isSuccess = false;
      state.logout.isError = "Something went wrong...";
    });
    builder.addCase(resetPassword.pending, (state, action) => {
      state.resetPassword.isLoading = true;
    });
    builder.addCase(resetPassword.rejected, (state, action) => {
      state.resetPassword.isLoading = false;
      //@ts-ignore
      state.resetPassword.isError = action.payload;
    });
    builder.addCase(resetPassword.fulfilled, (state, action) => {
      state.resetPassword.isLoading = false;
      state.resetPassword.isSuccess = true;
    });
    builder.addCase(completePasswordReset.pending, (state) => {
      state.completePasswordReset.isLoading = true;
      state.completePasswordReset.isSuccess = false;
      state.completePasswordReset.isError = null;
    });
    builder.addCase(completePasswordReset.fulfilled, (state) => {
      state.completePasswordReset.isLoading = false;
      state.completePasswordReset.isSuccess = true;
      state.completePasswordReset.isError = null;
    });
    builder.addCase(completePasswordReset.rejected, (state, action) => {
      state.completePasswordReset.isLoading = false;
      state.completePasswordReset.isSuccess = false;
      state.completePasswordReset.isError = null;
    });
    builder.addCase(resetPasswordResetState, (state) => {
      state.completePasswordReset.isLoading = false;
      state.completePasswordReset.isSuccess = false;
      state.completePasswordReset.isError = null;
    });
    builder.addCase(verifyResetLink.pending, (state) => {
      state.resetLink.isLoading = true;
      state.resetLink.isSuccess = false;
      state.resetLink.isError = null;
    });
    builder.addCase(verifyResetLink.fulfilled, (state, action) => {
      state.resetLink.isLoading = false;
      state.resetLink.isSuccess = true; 
      state.resetLink.isValid = action.payload.isValid; 
      state.resetLink.isError = null;
    });
    builder.addCase(verifyResetLink.rejected, (state, action) => {
      state.resetLink.isLoading = false;
      state.resetLink.isSuccess = false;
      state.resetLink.isError = action.error.message || "Failed to verify reset link";
    });
    builder.addCase(resetVerifyResetLink, (state) => {
      state.resetLink.isLoading = false;
      state.resetLink.isSuccess = false;
      state.resetLink.isError = null;
    });

    // ⭐ Activate Account
    builder.addCase(AuthActivateAccount.pending, (state, action) => { state.signUp.isLoading = true; });
    builder.addCase(AuthActivateAccount.rejected, (state, action: any) => {
      state.signUp.isLoading = false;
      state.signUp.errorMessage = action.payload?.message || "Something went wrong";
    });
    builder.addCase(AuthActivateAccount.fulfilled, (state, action) => {
      state.signUp.isLoading = false;
      state.signUp.isSuccess = true;
      state.signUp.user = action.payload?.data || null;
      state.signUp.successMessage = action.payload?.message || "Something went wrong";
      localStorage.setItem("accessToken", action.payload?.data?.jwtToken)
    });
    // ⭐ Sign Up
    builder.addCase(signUp.pending, (state, action) => { state.signUp.isLoading = true; });
    builder.addCase(signUp.rejected, (state, action: any) => {
      state.signUp.isLoading = false;
      state.signUp.errorMessage = action.payload?.message || "Something went wrong";
    });
    builder.addCase(signUp.fulfilled, (state, action) => {
      state.signUp.isLoading = false;
      state.signUp.isSuccess = true;
      state.signUp.user = action.payload?.data || null;
      state.signUp.successMessage = action.payload?.message || "Something went wrong";
      localStorage.setItem("accessToken", action.payload?.data?.jwtToken)
    });
  },
});
export const {
  reset,
  resetSignIn,
  resetResetPassword,
  resetSignUp,
  resetAuthSlice,
} = authSlice.actions;
export const selectErrorMessage = (state: RootState) => state.auth.errorMessage;